import { DollarSign, Users, FileText, Smartphone, Calendar, BarChart3, Building2, CheckCircle2 } from "lucide-react";

export const features = [
  {
    icon: DollarSign,
    badge: "AI-Powered",
    title: { fr: "Automatisation Paie", en: "Payroll Automation", ar: "أتمتة الرواتب" },
    description: {
      fr: "Calcul automatique des salaires avec détection d'anomalies par AI. Export comptable instantané et conformité locale garantie.",
      en: "Automatic salary calculation with AI anomaly detection. Instant accounting export and guaranteed local compliance.",
      ar: "حساب تلقائي للرواتب مع كشف الشذوذات بالذكاء الاصطناعي.",
    },
    highlights: [
      { fr: "Détection d'erreurs automatique", en: "Automatic error detection", ar: "كشف الأخطاء التلقائي" },
      { fr: "Export comptable en 1 clic", en: "1-click accounting export", ar: "تصدير محاسبي بنقرة واحدة" },
      { fr: "Conformité Code du Travail", en: "Labor Law compliance", ar: "الامتثال لقانون العمل" },
    ],
  },
  {
    icon: Users,
    title: {
      fr: "Recrutement Intelligent & ATS",
      en: "Smart Recruitment & ATS",
      ar: "التوظيف الذكي ونظام تتبع المتقدمين",
    },
    description: {
      fr: "Matching intelligent candidat-poste avec AI. Screening automatique des CVs et pipeline visuel.",
      en: "Intelligent candidate-job matching with AI. Automatic CV screening and visual pipeline.",
      ar: "مطابقة ذكية بين المرشح والوظيفة بالذكاء الاصطناعي.",
    },
    highlights: [
      { fr: "Matching AI candidat-poste", en: "AI candidate-job matching", ar: "مطابقة المرشحين" },
      { fr: "Screening automatique CVs", en: "Automatic CV screening", ar: "فحص السير الذاتية" },
      { fr: "Pipeline visuel", en: "Visual pipeline", ar: "خط أنابيب مرئي" },
    ],
  },
  {
    icon: FileText,
    badge: "Core",
    title: { fr: "Gestion Employés", en: "Employee Management", ar: "إدارة الموظفين" },
    description: {
      fr: "Base de données complète avec historique, self-service employés et documents digitaux.",
      en: "Complete database with history, employee self-service and digital documents.",
      ar: "قاعدة بيانات كاملة مع التاريخ والخدمة الذاتية.",
    },
    highlights: [
      { fr: "Historique complet", en: "Complete history", ar: "تاريخ كامل" },
      { fr: "Self-service employés", en: "Employee self-service", ar: "خدمة ذاتية" },
      { fr: "Import en masse", en: "Bulk import", ar: "استيراد جماعي" },
    ],
  },
  {
    icon: Smartphone,
    badge: "Mobile",
    title: { fr: "Pointage Mobile", en: "Mobile Attendance", ar: "الحضور المحمول" },
    description: {
      fr: "Check-in mobile avec géolocalisation. Intégration ZKTeco et multi-sites.",
      en: "Mobile check-in with geolocation. ZKTeco integration and multi-site.",
      ar: "تسجيل الحضور عبر الهاتف المحمول.",
    },
    highlights: [
      { fr: "Check-in géolocalisé", en: "Geolocated check-in", ar: "تسجيل محدد الموقع" },
      { fr: "Intégration ZKTeco", en: "ZKTeco integration", ar: "تكامل ZKTeco" },
      { fr: "Multi-sites", en: "Multi-site", ar: "متعدد المواقع" },
    ],
  },
  {
    icon: Calendar,
    badge: "Smart",
    title: { fr: "Gestion Congés", en: "Leave Management", ar: "إدارة الإجازات" },
    description: {
      fr: "Automatisation complète avec validation hiérarchique et calcul heures sup.",
      en: "Complete automation with hierarchical validation and overtime calculation.",
      ar: "أتمتة كاملة مع التحقق الهرمي.",
    },
    highlights: [
      { fr: "Validation hiérarchique", en: "Hierarchical validation", ar: "التحقق الهرمي" },
      { fr: "Calcul heures sup", en: "Overtime calculation", ar: "حساب العمل الإضافي" },
      { fr: "Planning flexible", en: "Flexible scheduling", ar: "جدولة مرنة" },
    ],
  },
  {
    icon: BarChart3,
    badge: "Insights",
    title: { fr: "Analytics RH", en: "HR Analytics", ar: "تحليلات الموارد البشرية" },
    description: {
      fr: "Dashboards interactifs avec KPIs temps réel et prédictions AI.",
      en: "Interactive dashboards with real-time KPIs and AI predictions.",
      ar: "لوحات معلومات تفاعلية مع مؤشرات الأداء.",
    },
    highlights: [
      { fr: "Dashboards temps réel", en: "Real-time dashboards", ar: "لوحات فورية" },
      { fr: "Prédictions AI", en: "AI predictions", ar: "توقعات الذكاء الاصطناعي" },
      { fr: "Exports automatisés", en: "Automated exports", ar: "صادرات تلقائية" },
    ],
  },
];

export const howItWorksSteps = [
  {
    step: "01",
    title: { fr: "Inscription & Configuration", en: "Sign Up & Setup", ar: "التسجيل والإعداد" },
    description: {
      fr: "Créez votre compte en 2 minutes et configurez votre entreprise",
      en: "Create your account in 2 minutes and configure your company",
      ar: "أنشئ حسابك في دقيقتين وقم بتكوين شركتك",
    },
    icon: Building2,
  },
  {
    step: "02",
    title: { fr: "Import des Données", en: "Data Import", ar: "استيراد البيانات" },
    description: {
      fr: "Importez vos employés en masse via Excel ou manuellement",
      en: "Import your employees in bulk via Excel or manually",
      ar: "استورد موظفيك بالجملة عبر Excel أو يدويًا",
    },
    icon: FileText,
  },
  {
    step: "03",
    title: { fr: "Intégration Matériel", en: "Hardware Integration", ar: "تكامل الأجهزة" },
    description: {
      fr: "Connectez vos terminaux ZKTeco et systèmes existants",
      en: "Connect your ZKTeco terminals and existing systems",
      ar: "قم بتوصيل أجهزة ZKTeco والأنظمة الموجودة",
    },
    icon: Smartphone,
  },
  {
    step: "04",
    title: { fr: "Lancement & Formation", en: "Launch & Training", ar: "الإطلاق والتدريب" },
    description: {
      fr: "Formation de vos équipes et mise en production",
      en: "Train your teams and go live",
      ar: "قم بتدريب فرقك وانتقل إلى الإنتاج",
    },
    icon: CheckCircle2,
  },
];

export const integrations = [
  { name: "ZKTeco", dot: "#2563eb" },
  { name: "SAP", dot: "#0070f3" },
  { name: "Microsoft 365", dot: "#ea4300" },
  { name: "Oracle", dot: "#c74634" },
  { name: "Slack", dot: "#E01E5A" },
  { name: "Google Workspace", dot: "#34a853" },
  { name: "Zapier", dot: "#FF4A00" },
  { name: "Teams", dot: "#6264A7" },
  { name: "Langchain", dot: "#5e6ad2" },
  { name: "Odoo", dot: "#714B67" },
  { name: "SuccessFactors", dot: "#f0a500" },
  { name: "Workday", dot: "#f5821f" },
];

export const pricingPlans = [
  {
    code: "STARTER",
    name: { fr: "Starter", en: "Starter", ar: "المبتدئ" },
    description: { fr: "Pour les petites entreprises", en: "For small businesses", ar: "للشركات الصغيرة" },
    price: { monthly: "$49", annual: "$39" },
    currency: { fr: "USD / mois", en: "USD / month", ar: "دولار / شهر" },
    features: [
      { fr: "Jusqu'à 50 employés", en: "Up to 50 employees", ar: "حتى 50 موظفًا" },
      { fr: "Gestion de base", en: "Basic management", ar: "إدارة أساسية" },
      { fr: "Pointage mobile", en: "Mobile attendance", ar: "حضور محمول" },
      { fr: "Support email", en: "Email support", ar: "دعم البريد الإلكتروني" },
    ],
    cta: { fr: "Commencer", en: "Get Started", ar: "ابدأ" },
    popular: false,
  },
  {
    code: "PROFESSIONAL",
    name: { fr: "Professional", en: "Professional", ar: "المحترف" },
    description: { fr: "Pour les entreprises en croissance", en: "For growing businesses", ar: "للشركات المتنامية" },
    price: { monthly: "$129", annual: "$103" },
    currency: { fr: "USD / mois", en: "USD / month", ar: "دولار / شهر" },
    features: [
      { fr: "Jusqu'à 200 employés", en: "Up to 200 employees", ar: "حتى 200 موظف" },
      { fr: "Toutes les fonctionnalités", en: "All features", ar: "جميع الميزات" },
      { fr: "AI Payroll & Recruitment", en: "AI Payroll & Recruitment", ar: "AI للرواتب والتوظيف" },
      { fr: "Support prioritaire", en: "Priority support", ar: "دعم ذو أولوية" },
      { fr: "Formation incluse", en: "Training included", ar: "تدريب مشمول" },
    ],
    cta: { fr: "Commencer", en: "Get Started", ar: "ابدأ" },
    popular: true,
  },
  {
    code: "ENTERPRISE",
    name: { fr: "Enterprise", en: "Enterprise", ar: "المؤسسي" },
    description: { fr: "Pour les grandes organisations", en: "For large organizations", ar: "للمنظمات الكبيرة" },
    price: { monthly: "Sur mesure", annual: "Sur mesure" },
    currency: { fr: "", en: "", ar: "" },
    features: [
      { fr: "Employés illimités", en: "Unlimited employees", ar: "موظفون غير محدودين" },
      { fr: "Solutions personnalisées", en: "Custom solutions", ar: "حلول مخصصة" },
      { fr: "Intégrations dédiées", en: "Dedicated integrations", ar: "تكاملات مخصصة" },
      { fr: "Support 24/7", en: "24/7 support", ar: "دعم 24/7" },
      { fr: "SLA garanti", en: "Guaranteed SLA", ar: "SLA مضمون" },
    ],
    cta: { fr: "Nous contacter", en: "Contact Us", ar: "اتصل بنا" },
    popular: false,
  },
];

export const faqs = [
  {
    question: {
      fr: "Comment fonctionne l'essai gratuit ?",
      en: "How does the free trial work?",
      ar: "كيف تعمل الفترة التجريبية المجانية؟",
    },
    answer: {
      fr: "L'essai gratuit de 14 jours vous donne accès à toutes les fonctionnalités sans carte de crédit. Vous pouvez importer jusqu'à 50 employés et tester l'ensemble de la plateforme.",
      en: "The 14-day free trial gives you access to all features without a credit card. You can import up to 50 employees and test the entire platform.",
      ar: "تمنحك الفترة التجريبية المجانية لمدة 14 يومًا إمكانية الوصول إلى جميع الميزات دون بطاقة ائتمان.",
    },
  },
  {
    question: {
      fr: "Puis-je importer mes données existantes ?",
      en: "Can I import my existing data?",
      ar: "هل يمكنني استيراد بياناتي الحالية؟",
    },
    answer: {
      fr: "Oui, nous supportons l'import via Excel, CSV et API. Notre équipe vous assiste gratuitement dans la migration de vos données.",
      en: "Yes, we support import via Excel, CSV and API. Our team assists you free of charge in migrating your data.",
      ar: "نعم، ندعم الاستيراد عبر Excel وCSV وAPI. يساعدك فريقنا مجانًا في ترحيل بياناتك.",
    },
  },
  {
    question: {
      fr: "Est-ce conforme au Code du Travail tunisien ?",
      en: "Is it compliant with Tunisian Labor Law?",
      ar: "هل يتوافق مع قانون العمل التونسي؟",
    },
    answer: {
      fr: "Absolument. Mawered est conçu spécifiquement pour la Tunisie et respecte 100% du Code du Travail tunisien, y compris les dernières mises à jour.",
      en: "Absolutely. Mawered is designed specifically for Tunisia and complies 100% with Tunisian Labor Law, including the latest updates.",
      ar: "تماماً. تم تصميم Mawered خصيصًا لتونس ويتوافق بنسبة 100٪ مع قانون العمل التونسي.",
    },
  },
  {
    question: {
      fr: "Quel est le délai de mise en place ?",
      en: "What is the implementation timeline?",
      ar: "ما هو الجدول الزمني للتنفيذ؟",
    },
    answer: {
      fr: "La mise en place complète prend généralement 2-4 semaines incluant formation. Pour les petites structures, vous pouvez être opérationnel en moins d'une semaine.",
      en: "Full implementation typically takes 2-4 weeks including training. For small structures, you can be operational in less than a week.",
      ar: "يستغرق التنفيذ الكامل عادةً 2-4 أسابيع بما في ذلك التدريب.",
    },
  },
  {
    question: {
      fr: "Proposez-vous une formation ?",
      en: "Do you offer training?",
      ar: "هل تقدمون التدريب؟",
    },
    answer: {
      fr: "Oui, tous nos plans incluent une formation complète : webinaires, documentation détaillée et support dédié. Le plan Enterprise inclut une formation sur site.",
      en: "Yes, all our plans include complete training: webinars, detailed documentation and dedicated support. The Enterprise plan includes on-site training.",
      ar: "نعم، تتضمن جميع خططنا تدريبًا كاملاً: ندوات عبر الإنترنت ووثائق مفصلة ودعم مخصص.",
    },
  },
];
